import React, { useContext, useEffect, useState } from "react";
import CommonModal from "../CommonModal";
import CustomTable from "../../CommonComponents/Table/CustomTable";
import { networkService } from "../../config/axios";
import DeleteOrgModal from "./DeleteOrgModal";
import UserInfoContext from "../../../utils/userInfoContext";
import { AdminActionIcons, AdminFormInput, AdminModalFooter } from "../../Admin/Shared";
import "../../Admin/Shared/AdminModal.css";

/* ----- Modal: Assign Master Domains to a SINGLE Organization -----*/
const OrganizationDomainsModal = ({ openModal, closeModal, company }) => {
    const { setShowNotifications } = useContext(UserInfoContext);

    const [activeTab, setActiveTab] = useState("list");
    const [organizationDomains, setOrganizationDomains] = useState([]);

    const [editingId, setEditingId] = useState(null);
    const [deleteId, setDeleteId] = useState(null);

    /* -------- AUTOCOMPLETE STATES -------- */
    const [domainSearch, setDomainSearch] = useState("");
    const [domainResults, setDomainResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [selectedDomainId, setSelectedDomainId] = useState(null);

    const [isDefault, setIsDefault] = useState(false);

    /* ---------------- Fetch Data ---------------- */
    useEffect(() => {
        if (!openModal) return;

        fetchOrganizationDomains();
    }, [openModal, activeTab, deleteId, company?.id]);

    const fetchOrganizationDomains = async () => {
        const res = await networkService(
            `/admin/organization_domains.json?company_id=${company?.id}`,
            "GET"
        );
        setOrganizationDomains(res?.data || []);
    };

    /* ---------------- Domain Search ---------------- */
    const searchDomains = async (value) => {
        if (!value || value.length < 2) {
            setDomainResults([]);
            return;
        }

        const res = await networkService(
            `/admin/master_domains.json?query=${value}`,
            "GET"
        );

        setDomainResults(res?.data?.master_domains || []);
    };

    /* ---------------- Submit ---------------- */
    const handleSubmit = async () => {
        try {
            const payload = {
                organization_domain: {
                    company_id: company?.id,
                    default: isDefault,
                    ...(selectedDomainId
                            ? { master_domain_id: selectedDomainId }
                            : { domain_name: domainSearch.trim() }
                    )
                }
            };

            if (editingId) {
                await networkService(
                    `/admin/organization_domains/${editingId}.json`,
                    "PUT",
                    payload
                );
                setShowNotifications({
                    showToast: true,
                    toastType: "success",
                    toastMessage: "Organization domain updated",
                });
            } else {
                await networkService(
                    "/admin/organization_domains.json",
                    "POST",
                    payload
                );
                setShowNotifications({
                    showToast: true,
                    toastType: "success",
                    toastMessage: "Domain assigned to organization",
                });
            }

            resetForm();
            setActiveTab("list");
            fetchOrganizationDomains();
        } catch (error) {
            const message =
                error?.response?.data?.errors?.join(", ") ||
                "Something went wrong";

            setShowNotifications({
                showToast: true,
                toastType: "danger",
                toastMessage: message,
            });
        }
    };

    /* ---------------- Delete ---------------- */
    const confirmDelete = async () => {
        try {
            await networkService(
                `/admin/organization_domains/${deleteId}.json`,
                "DELETE"
            );
            setDeleteId(null);
            setShowNotifications({
                showToast: true,
                toastType: "success",
                toastMessage: "Domain removed from organization",
            });
            fetchOrganizationDomains();
        } catch (error) {
            const message =
                error?.response?.data?.errors?.join(", ") ||
                "Failed to delete";

            setShowNotifications({
                showToast: true,
                toastType: "danger",
                toastMessage: message,
            });
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setDeleteId(null);
        setSelectedDomainId(null);
        setDomainSearch("");
        setDomainResults([]);
        setShowResults(false);
        setIsDefault(false);
    };

    /* ---------------- Table Columns ---------------- */
    const columns = [
        {
            Header: "Domain",
            accessor: row => row.master_domain?.name,
        },
        {
            Header: "Default",
            accessor: row => (row.default ? "Yes" : "No"),
        },
        {
            Header: "Actions",
            Cell: ({ row: { original } }) => (
                <AdminActionIcons
                    itemName={original.master_domain?.name}
                    onEdit={() => {
                        setEditingId(original.id);
                        setDomainSearch(original.master_domain?.name);
                        setSelectedDomainId(original.master_domain_id);
                        setIsDefault(original.default);
                        setActiveTab("form");
                    }}
                    onDelete={() => setDeleteId(original.id)}
                />
            ),
        },
    ];

    return (
        <>
            <DeleteOrgModal
                title="Are you sure?"
                openModal={deleteId !== null}
                closeModal={() => setDeleteId(null)}
                name="remove this domain from organization"
                handleDelete={confirmDelete}
            />

            <CommonModal
                title={`Manage ${company?.name} Domains`}
                modalStatus={openModal}
                changeActiveStatus={closeModal}
                classList="admin-modal admin-org-domains-modal"
                centered
                showFooter={false}
            >
                {activeTab === "list" ? (
                    <div className="org-domains-list-view">
                        <div className="admin-table-wrapper">
                            <CustomTable
                                data={organizationDomains}
                                columnData={columns}
                                showFilters={false}
                            />
                        </div>
                        <div className="admin-list-footer">
                            <button
                                type="button"
                                className="admin-cta-button"
                                onClick={() => {
                                    resetForm();
                                    setActiveTab("form");
                                }}
                            >
                                + ADD Domain
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="org-domains-form-view" style={{ maxWidth: '600px', margin: '0 auto' }}>
                        {/* DOMAIN AUTOCOMPLETE */}
                        <div className="position-relative mb-3">
                            <label className="admin-form-label">Domain Name</label>
                            <input
                                type="text"
                                className="admin-form-input"
                                placeholder="Enter domain name (example.com)"
                                value={domainSearch}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setDomainSearch(value);
                                    setSelectedDomainId(null);
                                    setShowResults(true);
                                    searchDomains(value);
                                }}
                                onBlur={() =>
                                    setTimeout(() => setShowResults(false), 200)
                                }
                            />

                            {showResults && domainResults.length > 0 && (
                                <ul className="list-group position-absolute w-100 z-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {domainResults.map((domain) => (
                                        <li
                                            key={domain.id}
                                            className="list-group-item list-group-item-action cursor-pointer"
                                            onClick={() => {
                                                setDomainSearch(domain.name);
                                                setSelectedDomainId(domain.id);
                                                setShowResults(false);
                                            }}
                                        >
                                            {domain.name}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="form-check mb-3">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="defaultDomain"
                                checked={isDefault}
                                onChange={(e) => setIsDefault(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="defaultDomain">
                                Set as Default Domain
                            </label>
                        </div>

                        <AdminModalFooter
                            onCancel={() => {
                                resetForm();
                                setActiveTab("list");
                            }}
                            onSubmit={handleSubmit}
                            submitText={editingId ? "Update" : "Assign"}
                            submitType="button"
                        />
                    </div>
                )}
            </CommonModal>
        </>
    );
};

export default OrganizationDomainsModal;
